/**
 * Move transactions to another payee (TMMAPP-0124 item 15; TMMAPP-0123 item 24a): the payee picker
 * (search, Create, "Did you mean"), and for one line a choice — **Only this transaction** (`scope:
 * LINE`, the default) or **All transactions with this bank text** (`scope: GROUP`), which says how
 * many (the exact-alias dry run of the bank text). Several lines move one after another with `LINE`
 * scope, and the dialog ends on a summary with each failure. Every write is `PUT
 * …/transactions/{txId}/payee` with the line's served `accountId` and `transactionId`.
 */
import { useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { payeesApi, type NameOnLineScope, type Payee, type PayeeLine } from '@/api/payees';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { PayeePicker, type PayeePick } from './PayeePicker';

interface Failure {
  line: PayeeLine;
  message: string;
}

interface MovePayeeDialogProps {
  payee: Payee;
  lines: PayeeLine[];
  onClose: () => void;
  /** After any line moved: the page re-reads the payee. */
  onMoved: () => Promise<void>;
  onNotify: (message: string) => void;
}

export function MovePayeeDialog({
  payee,
  lines,
  onClose,
  onMoved,
  onNotify,
}: MovePayeeDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const scopeId = useId();
  const single = lines.length === 1 ? lines[0] : undefined;
  const [pick, setPick] = useState<PayeePick | null>(null);
  const [scope, setScope] = useState<NameOnLineScope>('LINE');
  const [groupCount, setGroupCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ moved: number; failures: Failure[] } | null>(null);
  const dirty = summary === null && (pick !== null || scope !== 'LINE');
  const guard = useDialogDirtyGuard(dirty, onClose, busy);

  // "All transactions with this bank text" says how many: the exact-alias dry run of that text.
  const bankText = single?.payee ?? '';
  useEffect(() => {
    if (bankText.trim().length === 0) return undefined;
    let cancelled = false;
    payeesApi
      .previewAlias(payee.id, { matchType: 'EXACT', pattern: bankText.trim() })
      .then((preview) => {
        if (!cancelled) setGroupCount(preview.matchCount);
      })
      .catch(() => {
        /* the choice reads without its count */
      });
    return () => {
      cancelled = true;
    };
  }, [payee.id, bankText]);

  const targetName = pick === null ? '' : 'payee' in pick ? pick.payee.name : pick.name;

  function bodyFor(lineScope: NameOnLineScope) {
    if (pick === null) throw new Error('no payee picked');
    return 'payee' in pick
      ? { payeeId: pick.payee.id, scope: lineScope }
      : { name: pick.name, scope: lineScope };
  }

  async function move() {
    if (busy || pick === null) return;
    setBusy(true);
    setError(null);
    if (single !== undefined) {
      try {
        await payeesApi.nameOnLine(single.accountId, single.transactionId, bodyFor(scope));
        await onMoved();
        onNotify(t('payee.move.moved', { payee: targetName }));
        onClose();
      } catch (err) {
        setError(errorCopy(t, err, 'payee.move.failed'));
      } finally {
        setBusy(false);
      }
      return;
    }
    // Several lines: one after another, each with LINE scope; a failure never stops the rest.
    let moved = 0;
    const failures: Failure[] = [];
    for (const line of lines) {
      try {
        await payeesApi.nameOnLine(line.accountId, line.transactionId, bodyFor('LINE'));
        moved += 1;
      } catch (err) {
        failures.push({ line, message: errorCopy(t, err, 'payee.move.failed') });
      }
    }
    if (moved > 0) await onMoved().catch(() => undefined);
    setBusy(false);
    if (failures.length === 0) {
      onNotify(t('payee.move.movedMany', { count: moved, payee: targetName }));
      onClose();
      return;
    }
    setSummary({ moved, failures });
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      data-move-payee-dialog=""
    >
      <DialogTitle id={titleId}>
        {single !== undefined
          ? t('payee.move.title')
          : t('payee.move.titleMany', { count: lines.length })}
      </DialogTitle>
      {summary !== null ? (
        <>
          <DialogContent>
            <Stack spacing={1.5}>
              <Typography role="status">
                {t('payee.move.summary', {
                  moved: summary.moved,
                  count: lines.length,
                  payee: targetName,
                })}
              </Typography>
              <Box component="ul" sx={{ m: 0, paddingInlineStart: 2.5 }} data-move-failures="">
                {summary.failures.map((failure) => (
                  <li key={failure.line.transactionId}>
                    <Typography variant="body2">
                      {t('payee.move.failure', {
                        date: formatCivilDate(failure.line.postedOn, locale),
                        payee: failure.line.payee,
                        message: failure.message,
                      })}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <DialogDoneButton onClick={onClose} />
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {error !== null && <Alert severity="error">{error}</Alert>}
              {single !== undefined && (
                <Typography
                  variant="mono"
                  sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}
                >
                  {t('payee.bankText', { text: single.payee })}
                </Typography>
              )}
              <PayeePicker
                label={t('payee.move.to')}
                value={pick}
                onChange={(next) => {
                  setPick(next);
                  setError(null);
                }}
                exclude={payee.id}
                disabled={busy}
              />
              {single !== undefined && (
                <FormControl>
                  <FormLabel id={scopeId}>{t('payee.move.scopeLabel')}</FormLabel>
                  <RadioGroup
                    aria-labelledby={scopeId}
                    value={scope}
                    onChange={(event) => setScope(event.target.value as NameOnLineScope)}
                  >
                    <FormControlLabel
                      value="LINE"
                      control={<Radio />}
                      label={t('payee.move.scopeLine')}
                    />
                    <FormControlLabel
                      value="GROUP"
                      control={<Radio />}
                      label={
                        groupCount !== null
                          ? t('payee.move.scopeGroupCount', {
                              count: groupCount,
                            })
                          : t('payee.move.scopeGroup')
                      }
                    />
                  </RadioGroup>
                </FormControl>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={guard.requestClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <SubmitButton loading={busy} disabled={pick === null} onClick={() => void move()}>
              {t('payee.move.submit')}
            </SubmitButton>
          </DialogActions>
        </>
      )}
    </GuardedDialog>
  );
}
